import { Edit, Eye, Plus, Search, Trash2, Wrench } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import API_BASE_URL, { API_ENDPOINTS, buildApiUrl } from '../config/api.js';
import { useAuth } from '../contexts/AuthContext';
import './Mantenimiento.css';

const Mantenimiento = () => {
  const { authenticatedFetch } = useAuth();
  const [maintenances, setMaintenances] = useState([]);
  const [filteredMaintenances, setFilteredMaintenances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingMaintenance, setEditingMaintenance] = useState(null);
  const [viewingMaintenance, setViewingMaintenance] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [vehicles, setVehicles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    vehicleId: '',
    employeeId: '',
    description: '',
    status: 'PENDING',
    technician: '',
    scheduledStartAt: '',
    scheduledEndAt: '',
    isCompleted: false
  });



  const statusOptions = [
    { value: 'PENDING', label: 'Pendiente', color: 'pending' },
    { value: 'IN_PROGRESS', label: 'En Progreso', color: 'in_progress' },
    { value: 'COMPLETED', label: 'Completado', color: 'completed' },
    { value: 'CANCELLED', label: 'Cancelado', color: 'cancelled' }
  ];

  useEffect(() => {
    fetchMaintenances();
    fetchVehicles();
    fetchEmployees();
  }, []);

  // Filtrar mantenimientos cuando cambie el término de búsqueda
  useEffect(() => {
    const filtered = maintenances.filter(maintenance =>
      maintenance.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      maintenance.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
      maintenance.technician.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (maintenance.bicycle?.model?.name && maintenance.bicycle.model.name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredMaintenances(filtered);
    setCurrentPage(1);
  }, [maintenances, searchTerm]);

  // Calcular mantenimientos para la página actual
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentMaintenances = filteredMaintenances.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredMaintenances.length / itemsPerPage);

  const fetchMaintenances = async () => {
    try {
      setLoading(true);
      console.log('🔧 Cargando mantenimientos desde:', buildApiUrl(API_ENDPOINTS.MAINTENANCES));
      
      const response = await authenticatedFetch(buildApiUrl(API_ENDPOINTS.MAINTENANCES));
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Mantenimientos cargados:', data);
        setMaintenances(data);
      } else if (response.status === 403) {
        setError('No tienes permisos para acceder a esta funcionalidad');
      } else {
        console.error('❌ Error al cargar mantenimientos:', response.status, response.statusText);
        setError(`Error al cargar mantenimientos: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('❌ Error de conexión:', error);
      setError('Error de conexión al servidor. Verifica que el backend esté ejecutándose.');
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/vehicles`);
      if (response.ok) {
        const data = await response.json();
        setVehicles(data);
      }
    } catch (error) {
      console.error('Error al cargar vehículos:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await authenticatedFetch(buildApiUrl(API_ENDPOINTS.USERS));
      if (response.ok) {
        const data = await response.json();
        setEmployees(data);
      }
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      vehicleId: '',
      employeeId: '',
      description: '',
      status: 'PENDING',
      technician: '',
      scheduledStartAt: '',
      scheduledEndAt: '',
      isCompleted: false
    });
    setEditingMaintenance(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingMaintenance 
        ? buildApiUrl(`${API_ENDPOINTS.MAINTENANCES}/${editingMaintenance.id}`)
        : buildApiUrl(API_ENDPOINTS.MAINTENANCES);
      
      console.log('🔧 Enviando datos a:', url);
      console.log('📦 Datos:', formData);
      
      const response = await authenticatedFetch(url, {
        method: editingMaintenance ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Mantenimiento guardado exitosamente:', result);
        await fetchMaintenances();
        setShowForm(false);
        resetForm();
        alert(editingMaintenance ? 'Mantenimiento actualizado exitosamente' : 'Mantenimiento creado exitosamente');
      } else {
        const errorData = await response.json();
        console.error('❌ Error del servidor:', errorData);
        
        if (response.status === 401) {
          alert('Error: No tienes permisos para realizar esta acción. Verifica tu autenticación.');
        } else if (response.status === 403) {
          alert('Error: Acceso denegado. Solo los administradores pueden gestionar mantenimientos.');
        } else {
          alert(`Error: ${errorData.message || 'Error al guardar el mantenimiento'}`);
        }
      }
    } catch (error) {
      console.error('❌ Error de conexión:', error);
      alert('Error de conexión al servidor. Verifica que el backend esté ejecutándose.');
    }
  };

  const handleEdit = (maintenance) => {
    setEditingMaintenance(maintenance);
    setFormData({
      vehicleId: maintenance.bicycleId,
      employeeId: maintenance.employeeId,
      description: maintenance.description,
      status: maintenance.status,
      technician: maintenance.technician,
      scheduledStartAt: maintenance.scheduledAt
        ? new Date(maintenance.scheduledAt).toISOString().slice(0, 16)
        : '',
      scheduledEndAt: '',
      isCompleted: maintenance.isCompleted || false
    });
    setShowForm(true);
  };

  const handleView = (maintenance) => {
    setViewingMaintenance(maintenance);
  };

  const handleComplete = async (maintenanceId) => {
    if (!confirm('¿Estás seguro de que quieres marcar este mantenimiento como completado?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(
        buildApiUrl(`${API_ENDPOINTS.MAINTENANCES}/${maintenanceId}/complete`),
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.ok) {
        await fetchMaintenances();
        alert('Mantenimiento marcado como completado');
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.message || 'Error al completar el mantenimiento'}`);
      }
    } catch (error) {
      console.error('❌ Error de conexión:', error);
      alert('Error de conexión al servidor. Verifica que el backend esté ejecutándose.');
    }
  };

  const handleDelete = async (maintenanceId) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este mantenimiento?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.MAINTENANCES}/${maintenanceId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchMaintenances();
        alert('Mantenimiento eliminado exitosamente');
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.message || 'Error al eliminar el mantenimiento'}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al eliminar el mantenimiento');
    }
  };

  const getStatusLabel = (status) => {
    const statusObj = statusOptions.find(s => s.value === status);
    return statusObj ? statusObj.label : status;
  };

  const getStatusColor = (status) => {
    const statusObj = statusOptions.find(s => s.value === status);
    return statusObj ? statusObj.color : 'default';
  };

  if (loading) {
    return (
      <div className="mantenimiento-page">
        <div className="loading">Cargando mantenimientos...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mantenimiento-page">
        <div className="error-message">
          <h2>Error de Conexión</h2>
          <p>{error}</p>
          <button 
            className="btn-primary"
            onClick={() => {
              setError(null);
              fetchMaintenances();
            }}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mantenimiento-page">
      <div className="page-header">
        <h1>Gestión de Mantenimientos</h1>
        <button 
          className="btn-primary"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          <Plus size={16} />
          Nuevo Mantenimiento
        </button>
      </div>

      {/* Buscador y controles */}
      <div className="table-controls">
        <div className="search-container">
          <Search size={16} />
          <input
            type="text"
            placeholder="Buscar por descripción, estado, técnico o vehículo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="pagination-controls">
          <label>
            Mostrar:
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="items-per-page-select"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
            </select>
          </label>
        </div>
      </div>

      <div className="table-container">
        {filteredMaintenances.length === 0 ? (
          <div className="no-maintenances">
            {searchTerm ? (
              <p>No se encontraron mantenimientos que coincidan con "{searchTerm}"</p>
            ) : (
              <p>No hay mantenimientos registrados</p>
            )}
            {!searchTerm && (
              <button 
                className="btn-primary"
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
              >
                Registrar el primer mantenimiento
              </button>
            )}
          </div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Vehículo</th>
                  <th>Descripción</th>
                  <th>Estado</th>
                  <th>Técnico</th>
                  <th>Usuario</th>
                  <th>Creado</th>
                  <th>Programado</th>
                  <th>Completado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {currentMaintenances.map((maintenance) => (
                  <tr key={maintenance.id}>
                    <td>#{maintenance.id}</td>
                    <td>
                      <div className="vehicle-info">
                        <span className="vehicle-model">
                          {maintenance.bicycle?.model?.name} {maintenance.bicycle?.model?.brand}
                        </span>
                        <span className="vehicle-details">
                          SN: {maintenance.bicycle?.serialNumber} | {maintenance.bicycle?.size}
                        </span>
                      </div>
                    </td>
                    <td>{maintenance.description}</td>
                    <td>
                      <span className={`status-badge ${getStatusColor(maintenance.status)}`}>
                        {getStatusLabel(maintenance.status)}
                      </span>
                    </td>
                    <td>{maintenance.technician}</td>
                    <td>{maintenance.employee?.name}</td>
                    <td>
                      <span className="created-date">
                        {new Date(maintenance.createdAt).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </td>
                    <td>
                      {maintenance.scheduledAt ? (
                        <span className="scheduled-date">
                          {new Date(maintenance.scheduledAt).toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      ) : (
                        <span className="no-schedule">Inmediato</span>
                      )}
                    </td>
                    <td>
                      {maintenance.isCompleted && maintenance.performedAt ? (
                        <span className="performed-date">
                          {new Date(maintenance.performedAt).toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      ) : (
                        <span className="not-completed">Pendiente</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon"
                          title="Ver detalles"
                          onClick={() => handleView(maintenance)}
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className="btn-icon"
                          title="Editar"
                          onClick={() => handleEdit(maintenance)}
                        >
                          <Edit size={16} />
                        </button>
                        {!maintenance.isCompleted && (
                          <button
                            className="btn-icon btn-success"
                            title="Completar"
                            onClick={() => handleComplete(maintenance.id)}
                          >
                            <Wrench size={16} />
                          </button>
                        )}
                        <button
                          className="btn-icon btn-danger"
                          title="Eliminar"
                          onClick={() => handleDelete(maintenance.id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Paginación */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="btn-secondary"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Anterior
                </button>
                
                <div className="page-numbers">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      className={`btn ${currentPage === page ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  className="btn-secondary"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Siguiente
                </button>
              </div>
            )}

            {/* Información de resultados */}
            <div className="results-info">
              Mostrando {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredMaintenances.length)} de {filteredMaintenances.length} mantenimientos
            </div>
          </>
        )}
      </div>

      {/* Formulario Modal */}
      {showForm && (
        <div className="form-overlay">
          <div className="form-container">
            <div className="form-header">
              <h2>{editingMaintenance ? 'Editar Mantenimiento' : 'Nuevo Mantenimiento'}</h2>
              <button 
                className="btn-close"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="maintenance-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Vehículo *</label>
                  <select
                    name="vehicleId"
                    value={formData.vehicleId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Seleccionar vehículo</option>
                    {vehicles.map(vehicle => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.model?.name} {vehicle.model?.brand} - {vehicle.serialNumber}
                        {vehicle.size != null && vehicle.size !== '' ? ` | Talla: ${vehicle.size}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Usuario *</label>
                  <select
                    name="employeeId"
                    value={formData.employeeId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Seleccionar usuario</option>
                    {employees.map(employee => (
                      <option key={employee.id} value={employee.id}>
                        {employee.name} ({employee.role})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Estado *</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    required
                  >
                    {statusOptions.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Técnico *</label>
                  <input
                    type="text"
                    name="technician"
                    value={formData.technician}
                    onChange={handleInputChange}
                    required
                    placeholder="Nombre del técnico"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Descripción *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  placeholder="Descripción detallada del mantenimiento..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Fecha inicio mantenimiento</label>
                  <input
                    type="datetime-local"
                    name="scheduledStartAt"
                    value={formData.scheduledStartAt}
                    onChange={handleInputChange}
                    placeholder="Seleccionar fecha y hora de inicio"
                  />
                </div>
                <div className="form-group">
                  <label>Fecha fin mantenimiento</label>
                  <input
                    type="datetime-local"
                    name="scheduledEndAt"
                    value={formData.scheduledEndAt}
                    onChange={handleInputChange}
                    placeholder="Seleccionar fecha y hora de fin (opcional)"
                  />
                </div>
              </div>
              <small className="form-help">
                Si solo indicas fecha de inicio se bloqueará ese día completo. Si indicas inicio y fin, se bloquearán todos los días del rango.
              </small>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isCompleted"
                    checked={formData.isCompleted}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      isCompleted: e.target.checked
                    }))}
                  />
                  <span className="checkbox-text">Marcar como completado</span>
                </label>
                <small className="form-help">
                  Si se marca, el mantenimiento se considerará completado al guardar
                </small>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {editingMaintenance ? 'Actualizar' : 'Crear'} Mantenimiento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Detalles */}
      {viewingMaintenance && (
        <div className="form-overlay">
          <div className="form-container">
            <div className="form-header">
              <h2>Detalles del Mantenimiento</h2>
              <button 
                className="btn-close"
                onClick={() => setViewingMaintenance(null)}
              >
                ×
              </button>
            </div>

            <div className="maintenance-details">
              <div className="detail-row">
                <strong>ID:</strong> #{viewingMaintenance.id}
              </div>
              <div className="detail-row">
                <strong>Vehículo:</strong> 
                <div className="vehicle-info">
                  <span className="vehicle-model">
                    {viewingMaintenance.bicycle?.model?.name} {viewingMaintenance.bicycle?.model?.brand}
                  </span>
                  <span className="vehicle-details">
                    SN: {viewingMaintenance.bicycle?.serialNumber} | {viewingMaintenance.bicycle?.size}
                  </span>
                </div>
              </div>
              <div className="detail-row">
                <strong>Descripción:</strong> {viewingMaintenance.description}
              </div>
              <div className="detail-row">
                <strong>Estado:</strong> 
                <span className={`status-badge ${getStatusColor(viewingMaintenance.status)}`}>
                  {getStatusLabel(viewingMaintenance.status)}
                </span>
              </div>
              <div className="detail-row">
                <strong>Técnico:</strong> {viewingMaintenance.technician}
              </div>
              <div className="detail-row">
                <strong>Usuario:</strong> {viewingMaintenance.employee?.name} ({viewingMaintenance.employee?.role})
              </div>
              <div className="detail-row">
                <strong>Fecha de Creación:</strong> 
                <span className="created-date">
                  {new Date(viewingMaintenance.createdAt).toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
              <div className="detail-row">
                <strong>Fecha Programada:</strong> 
                {viewingMaintenance.scheduledAt ? (
                  <span className="scheduled-date">
                    {new Date(viewingMaintenance.scheduledAt).toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                ) : (
                  <span className="no-schedule">Inmediato</span>
                )}
              </div>
              <div className="detail-row">
                <strong>Fecha Completado:</strong> 
                {viewingMaintenance.isCompleted && viewingMaintenance.performedAt ? (
                  <span className="performed-date">
                    {new Date(viewingMaintenance.performedAt).toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                ) : (
                  <span className="not-completed">Pendiente</span>
                )}
              </div>
              <div className="detail-row">
                <strong>Completado:</strong> 
                <span className={`completion-status ${viewingMaintenance.isCompleted ? 'completed' : 'pending'}`}>
                  {viewingMaintenance.isCompleted ? 'Sí' : 'No'}
                </span>
              </div>
            </div>

            <div className="form-actions">
              <button 
                className="btn-secondary" 
                onClick={() => setViewingMaintenance(null)}
              >
                Cerrar
              </button>
              <button 
                className="btn-primary"
                onClick={() => {
                  setViewingMaintenance(null);
                  handleEdit(viewingMaintenance);
                }}
              >
                Editar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Mantenimiento; 